<template>
  <v-layout align-center justify-center>
    <v-container fluid class="pa-4">

      <v-row class="mb-4" align="center">
        <v-col>
          <h2 class="text-h5 font-weight-bold text-left">
            Offene Abholungen nachholen
          </h2>
          <div class="text-caption grey--text text-left">
            Nur Aufgaben mit zugewiesener Tour und ohne Abholdatum werden angezeigt.
          </div>
        </v-col>
        <v-col cols="auto">
          <v-btn text @click="$router.push({ name: 'Home' })">
            ← Zurück
          </v-btn>
        </v-col>
      </v-row>

      
      <v-row v-if="loading" justify="center" class="mt-8">
        <v-progress-circular indeterminate color="blue darken-2" size="48" />
      </v-row>

      <!-- Empty state  -->
      <v-row v-else-if="tasks.length === 0" justify="center" class="mt-8">
        <v-col cols="12" class="text-center">
          <v-icon size="64" color="green lighten-2">mdi-check-circle-outline</v-icon>
          <div class="text-h6 mt-3 grey--text">Keine offenen Abholungen vorhanden.</div>
        </v-col>
      </v-row>

      <!-- Task list  -->
      <v-row v-else>
        <v-col
          v-for="task in tasks"
          :key="task.id"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card outlined class="pa-3" style="border-left: 4px solid #1565C0;">
            <v-card-title class="subtitle-1 font-weight-bold pb-0 text-left">
              {{ task.company }}
            </v-card-title>
            <v-card-subtitle class="pt-1 text-left">
              {{ task.street }}, {{ task.zipcode }} {{ task.city }}
            </v-card-subtitle>
            <v-card-text class="pb-1 text-left">
              <div><strong>Auftrags-ID:</strong> {{ task.id }}</div>
              <div><strong>Tour-ID:</strong> {{ task.tour_id }}</div>
              <div><strong>Geplantes Datum:</strong> {{ task.execution_date || '—' }}</div>
              <div><strong>Bestellmenge:</strong> {{ task.order_qty || '—' }}</div>
              
              <div v-if="task.note" class="mt-1 text-caption grey--text">
                {{ task.note }}
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                color="blue darken-2"
                dark
                small
                @click="openDialog(task)"
              >
                Nachholen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Completion Dialog  -->
      <v-dialog v-model="dialog" max-width="700" persistent scrollable>
        <v-card>
          <v-card-title class="headline blue darken-2 white--text">
            Abholung nachholen – #{{ selectedTask ? selectedTask.id : '' }}
          </v-card-title>

          <v-card-text class="pt-4">

            <!-- Customer info -->
            <div v-if="selectedTask" class="mb-4 text-left">
              <div class="subtitle-1 font-weight-bold">{{ selectedTask.company }}</div>
              <div class="body-2 grey--text">
                {{ selectedTask.street }}, {{ selectedTask.zipcode }} {{ selectedTask.city }}
              </div>
            </div>

            <v-divider class="mb-4" />

            <!-- Manual pickup date -->
            <div class="subtitle-2 font-weight-bold mb-2 text-left">Abholdatum (manuell)</div>
            <v-menu
              v-model="datePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="manualPickupDate"
                  label="Abholdatum wählen"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="manualPickupDate"
                @input="datePicker = false"
                locale="de-DE"
                :max="today"
              />
            </v-menu>

            <v-divider class="mb-4" />

            <!-- Items table -->
            <div class="subtitle-2 font-weight-bold mb-2 text-left">Positionen</div>
            <v-simple-table dense class="mb-4">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Artikel</th>
                    <th class="text-left">Bestellmenge</th>
                    <th class="text-left">Abgeholte Menge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="itemsLoading">
                    <td colspan="3" class="text-center py-3">
                      <v-progress-circular indeterminate size="20" color="blue darken-2" />
                    </td>
                  </tr>
                  <tr v-else v-for="(item, idx) in dialogItems" :key="item.id">
                    <td class="text-left">{{ item.article_name }}</td>
                    <td class="text-left">{{ item.order_qty }}</td>
                    <td class="text-left" style="width: 130px;">
                      <v-text-field
                        v-model="dialogItems[idx].pickup_qty"
                        type="number"
                        min="0"
                        dense
                        outlined
                        hide-details
                        style="max-width: 100px;"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-divider class="mb-4" />

            <!-- Pickup note -->
            <div class="subtitle-2 font-weight-bold mb-2 text-left">Abholnotiz (optional)</div>
            <v-text-field
              v-model="manualPickupNote"
              label="Notiz"
              outlined
              dense
              class="mb-4"
            />

            <v-divider class="mb-4" />

            <!-- Signature pad -->
            <div class="subtitle-2 font-weight-bold mb-2 text-left">Unterschrift Fahrer</div>
            <div style="
              width: 100%;
              height: 180px;
              background-color: #EEEEEE;
              border-radius: 4px;
              border: 1px solid #BDBDBD;
              position: relative;
              overflow: hidden;
              touch-action: none;
            ">
              <VueSignaturePad
                width="100%"
                height="180px"
                ref="signaturePad"
                :options="signatureOptions"
              />
            </div>
            <div class="text-left mt-1">
              <v-btn text small @click="clearSignature">
                <v-icon small left>mdi-eraser</v-icon>
                Unterschrift löschen
              </v-btn>
            </div>

            <!-- Error message -->
            <v-alert
              v-if="errorMsg"
              type="error"
              dense
              text
              class="mt-3"
            >
              {{ errorMsg }}
            </v-alert>

          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn
              text
              @click="closeDialog"
              :disabled="saving"
            >
              Abbrechen
            </v-btn>
            <v-spacer />
            <v-btn
              color="blue darken-2"
              dark
              :loading="saving"
              :disabled="saving || !manualPickupDate"
              @click="completeTask"
            >
              <v-icon left>mdi-check</v-icon>
              Abschließen & E-Mail senden
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Success Snackbar -->
      <v-snackbar v-model="snackbar" color="green" timeout="4000" top>
        {{ snackbarMsg }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">OK</v-btn>
        </template>
      </v-snackbar>

    </v-container>
  </v-layout>
</template>

<script>
import Vue from "vue";

export default {
  name: "AdminBackfill",

  data() {
    return {
     
      loading: false,
      tasks: [],

      dialog: false,
      selectedTask: null,
      dialogItems: [],
      itemsLoading: false,
      saving: false,
      errorMsg: "",

      signatureOptions: {
        penColor: "#000000",
        backgroundColor: "rgb(238,238,238)",
      },

      manualPickupDate: new Date().toISOString().substr(0, 10),
      manualPickupNote: "",
      datePicker: false,
      today: new Date().toISOString().substr(0, 10),
      snackbar: false,
      snackbarMsg: "",

      driver_id: "",
      driver_sign_text: "",
      img_sign_name: "",
      group_type_id: "",
      group_type_text: "",
      company: "",
      street: "",
      zipcode: "",
      city: "",
      pickup_note: "",
      rest: "0",
      temp_task_id: "",
      reported_qty: 0,
      items: [],
    };
  },

  created() {
    const role = localStorage.getItem("role");
    if (role !== "ADMIN") {
      this.$router.push({ name: "Home" });
      return;
    }
    this.loadOpenTasks();
  },

  methods: {

    async loadOpenTasks() {
      this.loading = true;
      try {
        const param = {
          table: "task",
          statement: "custom",
          type: "",
          where: "false",
          selector: "",
          value: "",
          short: "false",
          extension:
            "SELECT * FROM task WHERE (pickup_date IS NULL OR pickup_date = '') " +
            "AND tour_id IS NOT NULL AND tour_id != '' " +
            "AND execution_date IS NOT NULL AND execution_date != '' " +
            "AND STR_TO_DATE(execution_date, '%d.%m.%Y') < CURDATE() " +
            "ORDER BY id DESC",
        };
        const result = await this.$func.queryAPI(param, {});
        this.tasks = result || [];
      } catch (e) {
        console.error("loadOpenTasks error", e);
      } finally {
        this.loading = false;
      }
    },


    async openDialog(task) {
      this.selectedTask = task;
      this.manualPickupDate = new Date().toISOString().substr(0, 10);
      this.manualPickupNote = task.pickup_note || "";
      this.errorMsg = "";
      this.dialogItems = [];
      this.dialog = true;

      // Wait for dialog to render, then init signature pad
      await this.$nextTick();
      setTimeout(() => {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.clearSignature();
          this.$refs.signaturePad.resizeCanvas();
        }
      }, 300);

      this.itemsLoading = true;
      try {
        const param = {
          table: "task_items",
          statement: "select",
          type: "list",
          where: "true",
          selector: "temp_task_id",
          value: task.temp_task_id,
          short: "false",
          extension: "",
        };
        const result = await this.$func.queryAPI(param, {});
        this.dialogItems = (result || []).map((item) => ({
          ...item,
          pickup_qty: item.pickup_qty || item.order_qty || "0",
        }));
      } catch (e) {
        console.error("loadItems error", e);
        this.dialogItems = [];
      } finally {
        this.itemsLoading = false;
        // Resize once more after content is fully rendered
        setTimeout(() => {
          if (this.$refs.signaturePad) {
            this.$refs.signaturePad.resizeCanvas();
          }
        }, 100);
      }
    },

    closeDialog() {
      this.dialog = false;
      this.selectedTask = null;
      this.dialogItems = [];
      this.errorMsg = "";
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }
    },

    clearSignature() {
      this.$refs.signaturePad.clearSignature();
    },


    async completeTask() {
      this.errorMsg = "";

      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (isEmpty) {
        this.errorMsg = "Bitte Unterschrift eintragen.";
        return;
      }
      if (!this.manualPickupDate) {
        this.errorMsg = "Bitte Abholdatum wählen.";
        return;
      }

      this.saving = true;
      const taskId = this.selectedTask.id;

      try {
        // Step 1: Upload signature
        await this.uploadSignature(data, taskId);

        // Step 2: Save pickup_date on task
        await this.$func.queryAPI(
          {
            table: "task",
            statement: "update",
            type: "detail",
            where: "true",
            selector: "id",
            value: String(taskId),
            short: "false",
            extension: "",
          },
          {
            pickup_date: this.manualPickupDate,
            pickup_note: this.manualPickupNote,
          }
        );

        // Step 3: Save pickup_qty per item
        for (const item of this.dialogItems) {
          await this.$func.queryAPI(
            {
              table: "task_items",
              statement: "update",
              type: "detail",
              where: "true",
              selector: "id",
              value: String(item.id),
              short: "false",
              extension: "",
            },
            { pickup_qty: String(item.pickup_qty) }
          );
        }

        // Step 4: Send mail + generate PDF
        await this.sendMail(taskId);

        // Step 5: Remove task from list & close
        this.tasks = this.tasks.filter((t) => t.id !== taskId);
        this.snackbarMsg = `Auftrag #${taskId} erfolgreich abgeschlossen.`;
        this.snackbar = true;
        this.closeDialog();
      } catch (e) {
        console.error("completeTask error", e);
        this.errorMsg = "Fehler: " + e.message;
      } finally {
        this.saving = false;
      }
    },


    async uploadSignature(dataUrl, taskId) {
      const data_new = dataUrl.split(",");
      const byteCharacters = atob(data_new[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/png" });

      const formData = new FormData();
      formData.append("files[]", blob, "signature.png");
      formData.append("description", "signatur");
      formData.append("type", "signatur");
      formData.append("selector", "task_id");
      formData.append("value", String(taskId));
      formData.append("table", "images");

      await new Promise((resolve, reject) => {
        const xhttp = new XMLHttpRequest();
        xhttp.open("POST", Vue.prototype.$api_endpoint, true);
        xhttp.onreadystatechange = function () {
          if (this.readyState === 4) {
            if (this.status === 200) resolve(this.responseText);
            else reject(new Error("Signature upload failed: " + this.status));
          }
        };
        xhttp.send(formData);
      });
    },

    async sendMail(taskId) {
      this.reported_qty = 0;
      this.items = [];

      // Load task detail
      let result = await this.$func.queryAPI(
        {
          table: "task",
          statement: "select",
          type: "detail",
          where: "true",
          selector: "id",
          value: String(taskId),
          short: "false",
          extension: "",
        },
        {}
      );

      this.company       = result[0]["company"];
      this.group_type_id = result[0]["group_type_id"];
      this.pickup_note   = result[0]["pickup_note"];
      this.street        = result[0]["street"];
      this.city          = result[0]["city"];
      this.zipcode       = result[0]["zipcode"];
      this.rest          = result[0]["rest_before"] || "0";
      this.driver_id     = result[0]["driver_id"];
      this.temp_task_id  = result[0]["temp_task_id"];

      let email = result[0]["email"] || "";
      if (email !== "") {
        email = email + "; " + Vue.prototype.$sender_email;
      } else {
        email = Vue.prototype.$sender_email;
      }

      // Load driver_id from tour if task doesn't have one
      if (!this.driver_id || this.driver_id === "null") {
        const tourResult = await this.$func.queryAPI(
          {
            table: "tour",
            statement: "select",
            type: "detail",
            where: "true",
            selector: "id",
            value: this.selectedTask.tour_id,
            short: "false",
            extension: "",
          },
          {}
        );
        this.driver_id = tourResult.length > 0 ? tourResult[0]["driver_id"] : null;
      }

      // Load driver sign text
      if (this.driver_id && this.driver_id !== "null") {
        result = await this.$func.queryAPI(
          {
            table: "driver",
            statement: "select",
            type: "detail",
            where: "true",
            selector: "id",
            value: this.driver_id,
            short: "false",
            extension: "",
          },
          {}
        );
        this.driver_sign_text = result.length > 0 ? result[0]["sign_text"] : "";
      } else {
        this.driver_sign_text = localStorage.getItem("driver_name") || "Admin";
      }

      // Load group type text
      result = await this.$func.queryAPI(
        {
          table: "group_type",
          statement: "select",
          type: "detail",
          where: "true",
          selector: "id",
          value: this.group_type_id,
          short: "false",
          extension: "",
        },
        {}
      );
      this.group_type_text = result.length > 0 ? result[0]["text"] : "";

      // Load task items
      result = await this.$func.queryAPI(
        {
          table: "task_items",
          statement: "select",
          type: "list",
          where: "true",
          selector: "temp_task_id",
          value: this.temp_task_id,
          short: "false",
          extension: "",
        },
        {}
      );
      this.items = [];
      for (let i = 0; i < result.length; i++) {
        this.reported_qty =
          Number(this.reported_qty) + Number(result[i]["order_qty"]);
        this.items.push({
          id:           result[i]["id"],
          rating:       result[i]["rating"],
          order_qty:    result[i]["order_qty"],
          pickup_qty:   result[i]["pickup_qty"],
          article_name: result[i]["article_name"],
          price:        result[i]["price"],
        });
      }

      // Load signature image name
      result = await this.$func.queryAPI(
        {
          table: "images",
          statement: "custom",
          type: "",
          where: "false",
          selector: "",
          value: "",
          short: "false",
          extension:
            "SELECT * FROM images WHERE task_id='" + taskId + "' AND type='signatur'",
        },
        {}
      );
      this.img_sign_name = result.length !== 0 ? result[0]["name"] : "";

      // Load group_type_email recipients
      result = await this.$func.queryAPI(
        {
          table: "group_type_email",
          statement: "select",
          type: "list",
          where: "true",
          selector: "group_type_id",
          value: this.group_type_id,
          short: "false",
          extension: "",
        },
        {}
      );
      for (let i = 0; i < result.length; i++) {
        email =
          email === ""
            ? result[i]["email"]
            : email + "; " + result[i]["email"];
      }

      // Generate PDF
      await this.createUebergabeschein(taskId);

      // Send email
      const mailParam = {
        sendmail: "true",
        mailto:   email,
        mailfrom: Vue.prototype.$sender_email,
        subject:  "Abholschein - " + taskId,
        message:
          "Sehr geehrte Damen und Herren,  \\r\\n" +
          "\\r\\n" +
          "Vielen Dank für Ihren Abholauftrag dieser wurde soeben abgeholt. Anbei erhalten Sie den Abholschein als Beleg für Ihre Unterlagen." +
          "\\r\\n" +
          "Bei Reklamationen oder Fragen erreichen Sie uns telefonisch unter: " +
          Vue.prototype.$sender_telefon +
          " \\r\\n" +
          "\\r\\n" +
          "Mit freundlichen Grüssen \\r\\n" +
          "\\r\\n" +
          "\\r\\n" +
          "Ihr Team von " +
          Vue.prototype.$sender_name +
          " \\r\\n" +
          "\\r\\n" +
          "\\r\\n",
        file_name: "auftrag_" + taskId + ".pdf",
        file_path: "upload/" + "auftrag_" + taskId + ".pdf",
      };

      if (
        !window.location.hostname.includes("localhost") &&
        !window.location.hostname.includes("vercel")
      ) {
        await this.$func.queryAPI(mailParam, {});
      } else {
        alert("TEST MODUS (localhost) – E-Mail wurde nicht gesendet!");
      }
       // await this.$func.queryAPI(mailParam, {});
    },


    async createUebergabeschein(taskId) {
      const param = {
        vorlage_name:   "uebergabeschein.png",
        pdf_autor:      "Hüska GmbH",
        pdf_name:       "auftrag_" + taskId + ".pdf",
        pdf_title:      "Übergabeschein",
        pdf_subject:    "",
        pdf_name_crypt: "auftrag_" + taskId + ".pdf",
      };

      const data = {
        text: {
          0:  { text: this.company.substring(0, 50), x: "10", y: "40" },
          1:  { text: this.street,                   x: "10", y: "50" },
          2:  { text: this.zipcode,                  x: "10", y: "55" },
          3:  { text: this.city,                     x: "25", y: "55" },
          4:  { text: this.manualPickupDate,          x: "80", y: "80" },
          5:  { text: "Übergabeschein -Nr.: " + taskId, x: "10", y: "23" },
          6:  { text: this.group_type_text,           x: "135", y: "77" },
          7:  { text: this.rest,                      x: "80", y: "187" },
          8:  { text: this.pickup_note,               x: "10", y: "212" },
          9:  { text: "",                             x: "10", y: "245" },
          10: { text: "gez. " + this.driver_sign_text, x: "150", y: "245" },
          11: { text: "", x: "0", y: "5" },
          12: { text: "", x: "0", y: "5" },
          13: { text: "", x: "0", y: "5" },
          14: { text: "", x: "0", y: "5" },
          15: { text: "", x: "0", y: "5" },
        },
        image: {
          0: {
            name: this.img_sign_name,
            x: "10",
            y: "225",
            w: "40",
            h: "25",
          },
        },
      };

      let y = 110;
      for (let i = 0; i < this.items.length; i++) {
        data["text"][11 + i].text =
          this.items[i]["order_qty"] +
          "                                        " +
          this.items[i]["pickup_qty"] +
          "                              " +
          this.items[i]["article_name"] +
          "                     " +
          "160103";
        data["text"][11 + i].x = "10";
        data["text"][11 + i].y = y;
        y = Number(y) + 10;
      }

      const request =
        "param=" +
        encodeURIComponent(JSON.stringify(param)) +
        "&data=" +
        encodeURIComponent(JSON.stringify(data));

      const config = {
        method: "GET",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        responseType: "blob",
      };

      try {
        await Vue.prototype.$axios.get(
          Vue.prototype.$api_pdf_endpoint + "?" + request,
          config
        );
      } catch {
        console.warn("PDF generation failed (non-blocking)");
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: box-shadow 0.2s ease;
}
.v-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
}
</style>